import { useCallback, useEffect, useRef, useState } from 'react';

import safeTryPlayVideo from '@/utils/media/safeTryPlayVideo';

interface UseFeedVideoPlaybackParams {
  videoRef: React.RefObject<HTMLVideoElement>;
  isActive: boolean;
  shouldPreload: boolean;
}

// Minimum settle time before a slide acts on visibility — during a fast fling
// intermediate slides cross the paging threshold transiently and must neither
// start playing nor start downloading.
const SETTLE_DELAY_MS = 200;

/**
 * Per-slide video policy for the feed. The element stays mounted while its
 * slide is inside FeedSlideList's mount window, so its buffer survives
 * nearby scroll-back; slides that leave the window unmount and re-download
 * on return:
 *   - warm-up: one-shot `load()` after the slide settles in the preload window
 *   - autoplay: play after the settle delay while active; pause + rewind
 *     immediately on deactivation (effect cleanup cancels a pending play)
 *   - tap-to-pause: `togglePlayback` / `isUserPaused` for the tap layer
 *
 * `isUserPaused` means paused *by the user* — system pauses (buffer stalls,
 * OS interruptions) are not reflected or recovered. If devices show freezes,
 * borrow the stall recovery from `useVideoPlayback`.
 */
const useFeedVideoPlayback = ({
  videoRef,
  isActive,
  shouldPreload,
}: UseFeedVideoPlaybackParams) => {
  const [isUserPaused, setIsUserPaused] = useState(false);
  const hasStartedLoadingRef = useRef(false);
  const pendingAutoplayTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!shouldPreload || hasStartedLoadingRef.current) return undefined;
    const warmUpTimer = setTimeout(() => {
      const video = videoRef.current;
      if (!video || hasStartedLoadingRef.current) return;
      hasStartedLoadingRef.current = true;
      // `load()` resets the element, so never run it once data exists —
      // it would discard the buffer (and a playing video's position).
      if (video.readyState !== HTMLMediaElement.HAVE_NOTHING) return;
      video.preload = 'auto';
      video.load();
    }, SETTLE_DELAY_MS);
    return () => clearTimeout(warmUpTimer);
  }, [shouldPreload, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (!isActive) {
      video.pause();
      video.currentTime = 0;
      return undefined;
    }
    setIsUserPaused(false);
    const playTimer = setTimeout(() => {
      hasStartedLoadingRef.current = true; // play() forces loading too
      safeTryPlayVideo(video);
    }, SETTLE_DELAY_MS);
    pendingAutoplayTimerRef.current = playTimer;
    return () => clearTimeout(playTimer);
  }, [isActive, videoRef]);

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      safeTryPlayVideo(video);
      setIsUserPaused(false);
    } else {
      // A pause inside the settle window must also cancel the pending
      // autoplay — otherwise it fires and plays behind the pause icon.
      clearTimeout(pendingAutoplayTimerRef.current);
      video.pause();
      setIsUserPaused(true);
    }
  }, [videoRef]);

  return { isUserPaused, togglePlayback };
};

export default useFeedVideoPlayback;
